/**
 * Rate-limited logging utility
 * Prevents console spam by limiting same error key to once per cooldown period
 */

interface LogEntry {
  timestamp: number;
  count: number; // How many times this key was logged (for reference)
}

const logCache = new Map<string, LogEntry>();
const DEFAULT_COOLDOWN_MS = 15000; // 15 seconds

/**
 * Rate-limited console.log
 * @param key Unique identifier for this log message (e.g., 'reminder-fetch-error')
 * @param message Message to log
 * @param cooldownMs Cooldown period in milliseconds (default: 15s)
 */
export function logRateLimited(
  key: string,
  message: string | (() => string),
  cooldownMs: number = DEFAULT_COOLDOWN_MS
): void {
  const now = Date.now();
  const entry = logCache.get(key);
  
  if (!entry || (now - entry.timestamp) >= cooldownMs) {
    const msg = typeof message === 'function' ? message() : message;
    console.log(msg);
    
    logCache.set(key, {
      timestamp: now,
      count: (entry?.count || 0) + 1,
    });
  }
}

/**
 * Rate-limited console.error
 */
export function errorRateLimited(
  key: string,
  message: string | (() => string),
  cooldownMs: number = DEFAULT_COOLDOWN_MS
): void {
  const now = Date.now();
  const entry = logCache.get(key);
  
  if (!entry || (now - entry.timestamp) >= cooldownMs) {
    const msg = typeof message === 'function' ? message() : message;
    console.error(msg);
    
    logCache.set(key, {
      timestamp: now,
      count: (entry?.count || 0) + 1,
    });
  }
}

/**
 * Rate-limited console.warn
 */
export function warnRateLimited(
  key: string,
  message: string | (() => string),
  cooldownMs: number = DEFAULT_COOLDOWN_MS
): void {
  const now = Date.now();
  const entry = logCache.get(key);
  
  if (!entry || (now - entry.timestamp) >= cooldownMs) {
    const msg = typeof message === 'function' ? message() : message;
    console.warn(msg);
    
    logCache.set(key, {
      timestamp: now,
      count: (entry?.count || 0) + 1,
    });
  }
}

/**
 * Clear log cache (useful for testing or manual cleanup)
 */
export function clearLogCache(): void {
  logCache.clear();
}



